import React, { PureComponent, MouseEvent } from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import isEqual from 'lodash/isEqual';
import { History } from 'history';

// actions
import {
  createBranding as createBrandingAction,
  updateBranding as updateBrandingAction,
  // types
  UpdateBrandingPayload,
  CreateBrandingPayload,
} from 'ducks/merchantDucks/branding/actions';
import { resetForms as resetFormsAction } from 'ducks/merchantDucks/form/actions';

// types
import RootState from 'ducks/RootState';
import { BrandingDetails } from 'ducks/merchantDucks/branding/types';
import { Optional } from 'utils/types';
import { Feature } from 'ducks/merchantDucks/user/types';

// constants
import Rules from 'restrictions/roles/constants/rules';

// components
import Save from 'components/Save';
import Loader from 'components/Loader';
import ContentHolder from 'components/ContentHolder';
import { ChildrenContainer } from 'components/Modals/BaseModal';
import Preview from 'pages/MerchantPages/Settings/Branding/components/Preview';

interface StateProps {
  brandingDetails: BrandingDetails;
  roleRestrictions: Rules[];
  features: Array<Feature>;
}

interface DispatchProps {
  createBranding: (data: CreateBrandingPayload) => any;
  updateBranding: (data: UpdateBrandingPayload) => any;
  resetForms: () => any;
}

interface OwnProps {
  id?: string | number;
  history: History;
  filled: boolean;
  showDeleteModal?: () => void;
}

type Props = StateProps & DispatchProps & OwnProps;

interface State {
  coverImage: Optional<File>;
  logoImage: Optional<File>;
  coverPreview: string;
  logoPreview: string;
}

class SavePaywallForm extends PureComponent<Props, State> {
  saveRef: React.RefObject<Save> = React.createRef();

  previewRef: React.RefObject<Preview> = React.createRef();

  state: State = {
    coverImage: null,
    logoImage: null,
    coverPreview: '',
    logoPreview: '',
  };

  componentDidUpdate(prevProps: Props) {
    const { brandingDetails: prevBrandingDetails, history } = prevProps;
    const { brandingDetails } = this.props;

    if (!isEqual(prevBrandingDetails, brandingDetails) && brandingDetails.isCreated) {
      history.push(`/settings/branding/edit/${brandingDetails.id}`);
    }
  }

  onCoverDrop = (file: File) => {
    const filePreview = URL.createObjectURL(file);
    this.setState({ coverImage: file, coverPreview: filePreview });
  };

  onLogoDrop = (file: File) => {
    const filePreview = URL.createObjectURL(file);
    this.setState({ logoImage: file, logoPreview: filePreview });
  };

  clearLogo = () => {
    this.setState({
      logoImage: null,
      logoPreview: '',
    });
  };

  save = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { current: saveRef } = this.saveRef;
    const { current: previewRef } = this.previewRef;

    const { filled, createBranding, updateBranding, id } = this.props;
    const { coverImage, logoImage } = this.state;

    const title = saveRef && saveRef.getInputValues();
    let newBrandingDetails: any = previewRef && previewRef.getValues();
    newBrandingDetails = { ...newBrandingDetails, brand_name: title };

    if (!filled) {
      createBranding({ paywallConfig: newBrandingDetails, coverImage, logoImage });
    } else {
      updateBranding({ paywallConfig: newBrandingDetails, id: id as string, update: false, coverImage, logoImage });
    }
  };

  cancel = (e: MouseEvent<HTMLAnchorElement>) => {
    const { history } = this.props;
    e.preventDefault();
    history.push('/settings/branding');
  };

  hasRule = (rule: Rules) => {
    const { roleRestrictions } = this.props;
    return roleRestrictions.includes(rule);
  };

  render() {
    const { brandingDetails, showDeleteModal, resetForms, features } = this.props;
    const { coverPreview, logoPreview } = this.state;

    if (brandingDetails.isSaving) {
      return <Loader />;
    }

    const isReadOnly = this.hasRule(Rules.READONLY_BRANDING_THEMES);

    return (
      <>
        <Save
          ref={this.saveRef}
          brandingDetails={brandingDetails}
          save={this.save}
          cancel={this.cancel}
          showDeleteModal={showDeleteModal}
          isReadOnly={isReadOnly}
        />
        <ContentHolder>
          <ChildrenContainer>
            <Preview
              ref={this.previewRef}
              previewCoverImage={coverPreview}
              previewLogoImage={logoPreview}
              branding={brandingDetails}
              uploadCoverImage={this.onCoverDrop}
              uploadLogoImage={this.onLogoDrop}
              clearLogo={this.clearLogo}
              resetForm={resetForms}
              features={features}
              hasRule={this.hasRule}
            />
          </ChildrenContainer>
        </ContentHolder>
      </>
    );
  }
}

export const PlainSavePaywallForm = SavePaywallForm;

export const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (state) => ({
  brandingDetails: state.branding.brandingDetails,
  roleRestrictions: state.auth.user.roleRestrictions,
  features: state.auth.features,
});

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = {
  createBranding: createBrandingAction,
  updateBranding: updateBrandingAction,
  resetForms: resetFormsAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(SavePaywallForm);
